import { Tag as AntdTag, Tooltip } from "antd";
import { ColumnProps } from "antd/lib/table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IClientAddressOrderReport,
  IClientAddressReport,
} from "../../../models/client/response";
import EndPoints from "../../../services/end-points";
import Table from "../../general/table-components/table";
import Tag from "../../general/antd/tag";
import { getOrderStatus, getOrderStatusColor, OrderStatus } from "../../../models/order/enum";

interface IProps {
  data?: IClientAddressReport[];
  clientId?: number;
  includeOrders?: boolean;
  ordersLimit?: number;
}

const normalizeTableData = <T,>(value: unknown): T[] => {
  if (Array.isArray(value)) return value;

  if (value && typeof value === "object") {
    if ("addresses" in value && Array.isArray((value as any).addresses)) {
      return (value as any).addresses;
    }

    const responseData = (value as { data?: unknown }).data;
    if (Array.isArray(responseData)) return responseData;

    if (responseData && typeof responseData === "object") {
      if ("addresses" in responseData && Array.isArray((responseData as any).addresses)) {
        return (responseData as any).addresses;
      }
      const nestedData = (responseData as { data?: unknown }).data;
      if (Array.isArray(nestedData)) return nestedData;
      if (nestedData && typeof nestedData === "object" && "addresses" in nestedData && Array.isArray((nestedData as any).addresses)) {
        return (nestedData as any).addresses;
      }
    }
  }

  return [];
};

const LoactionsClientTable: React.FC<IProps> = ({
  data,
  clientId,
  includeOrders = true,
  ordersLimit = 100,
}) => {
  const { t } = useTranslation();
  const [addresses, setAddresses] = useState<IClientAddressReport[]>(
    normalizeTableData<IClientAddressReport>(data)
  );
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientId) {
      setAddresses(normalizeTableData<IClientAddressReport>(data));
      return;
    }

    let isMounted = true;

    const getAddressesReport = async () => {
      setLoading(true);
      try {
        const { data: report } = await EndPoints.client.getClientAddressesReport(
          clientId,
          {
            includeOrders,
            ordersLimit,
          }
        );

        if (isMounted) {
          setAddresses(normalizeTableData<IClientAddressReport>(report));
        }
      } catch (error) {
        if (isMounted) setAddresses(normalizeTableData<IClientAddressReport>(data));
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    getAddressesReport();

    return () => {
      isMounted = false;
    };
  }, [clientId, data, includeOrders, ordersLimit]);

  const renderBooleanTag = (value?: boolean, dangerWhenTrue = false) => {
    if (value === undefined || value === null) return <>-</>;

    const color = value ? (dangerWhenTrue ? "red" : "blue") : "green";

    return <AntdTag color={color}>{t(value ? "yes" : "no")}</AntdTag>;
  };

  const orderColumns: ColumnProps<IClientAddressOrderReport>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      align: "center",
    },
    {
      title: t("order_number"),
      dataIndex: "number",
      key: "number",
      align: "center",
      render: (number) => number ?? "-",
    },
    {
      title: t("order_status"),
      dataIndex: "statusName",
      key: "statusName",
      align: "center",
      render: (_, record) => {
        const status = Number(record?.status) as OrderStatus;
        return (
          <Tag
            title={getOrderStatus(status)}
            color={getOrderStatusColor(status)}
          />
        );
      },
    },
    {
      title: t("client_address"),
      dataIndex: "clientAddressSnapshot",
      key: "clientAddressSnapshot",
      align: "center",
      minWidth: 220,
      render: (clientAddressSnapshot) => clientAddressSnapshot ?? "-",
    },
    {
      title: t("location_changed"),
      dataIndex: "locationChanged",
      key: "locationChanged",
      align: "center",
      render: (locationChanged) => renderBooleanTag(locationChanged, true),
    },
  ];

  const columns: ColumnProps<IClientAddressReport>[] = [
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      render: (name) => name ?? "-",
    },
    {
      title: t("primary_address"),
      dataIndex: "isPrimary",
      align: "center",
      key: "isPrimary",
      render: (isPrimary) => renderBooleanTag(isPrimary),
    },
    {
      title: t("street"),
      dataIndex: "street",
      align: "center",
      key: "street",
      render: (street) => street ?? "-",
    },
    {
      title: t("construction"),
      dataIndex: "construction",
      align: "center",
      key: "construction",
      minWidth:120,
      render: (construction) => construction ?? "-",
    },
    {
      title: t("near"),
      dataIndex: "near",
      align: "center",
      key: "near",
      minWidth:120,
      render: (near) => near ?? "-",
    },
    {
      title: t("flat"),
      dataIndex: "flat",
      align: "center",
      key: "flat",
      render: (flat) => flat ?? "-",
    },
    {
      title: t("area"),
      dataIndex: "area",
      align: "center",
      key: "area",
      render: (_, record) => record?.area?.name ?? "-",
    },
    {
      title: t("discount_area"),
      dataIndex: "discountArea",
      align: "center",
      key: "discountArea",
      render: (_, record) => record?.discountArea?.name ?? "-",
    },
    {
      title: t("outside_area"),
      dataIndex: "isOutsideArea",
      align: "center",
      key: "isOutsideArea",
      render: (isOutsideArea) => renderBooleanTag(isOutsideArea, true),
    },
    {
      title: t("outside_discount_area"),
      dataIndex: "isOutsideDiscountArea",
      align: "center",
      key: "isOutsideDiscountArea",
      render: (isOutsideDiscountArea) => renderBooleanTag(isOutsideDiscountArea, true),
    },
    {
      title: t("orders_count"),
      dataIndex: "ordersCount",
      align: "center",
      key: "ordersCount",
      render: (ordersCount) => ordersCount ?? "-",
    },
    {
      title: t("completed_paid_orders_count"),
      dataIndex: "completedPaidOrdersCount",
      align: "center",
      key: "completedPaidOrdersCount",
      render: (completedPaidOrdersCount) => completedPaidOrdersCount ?? "-",
    },
    {
      title: t("same_location_addresses_count"),
      dataIndex: "sameLocationAddressesCount",
      align: "center",
      key: "sameLocationAddressesCount",
      render: (_, record) => (
        <>
          {record?.sameLocationAddressesCount ?? "-"}
          {record?.hasAmbiguousExactLocationMatch && (
            <Tooltip title={t("ambiguous_exact_location_match_tooltip")}>
              <AntdTag color="warning" style={{ marginInlineStart: 6 }}>
                {t("warning")}
              </AntdTag>
            </Tooltip>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Table<IClientAddressReport>
        rowKey="id"
        columns={columns}
        dataSource={addresses ?? []}
        loading={loading}
        size="small"
        expandable={{
          expandedRowRender: (record) => (
            <Table<IClientAddressOrderReport>
              rowKey="id"
              columns={orderColumns}
              dataSource={normalizeTableData<IClientAddressOrderReport>(record?.orders)}
              size="small"
            />
          ),
          rowExpandable: (record) =>
            normalizeTableData<IClientAddressOrderReport>(record?.orders).length > 0,
        }}
      />
    </>
  );
};

export default LoactionsClientTable;
